import React, { useEffect, useMemo, useState } from "react";

import { Label, LoadingIcon as Loading } from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import countryList from "react-select-country-list";

import Error from "Components/Error";
import { vatCountries } from "Constants/vatCountries";
import Button, { Link } from "ds/Button";
import Select from "ds/Select";
import getUrl from "Libs/getUrl";
import logger from "Libs/logger";
import client from "Libs/platform";
import { capitalize } from "Libs/utils";
import { updateOrganizationAddress } from "Reducers/organization/address";
import { organizationByDescriptionIdSelector } from "Reducers/organization/organization";
import { organizationProfileSelector } from "Reducers/organization/profile";
import { useAppDispatch } from "Store/hooks";

import {
  AddressWrapper,
  InlineFields,
  Layout,
  PaymentWrapper,
  Note,
  SelectWrapper,
  Input
} from "./AddressForm.styles";

const AddressForm = ({
  finalize,
  organizationId,
  loadedAddress,
  addressData,
  disableCountry,
  onEmailChange,
  onAddressChange,
  onDraftAddressChange,
  draftAddress
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { vat_number, company_name, billing_contact, ...draftAddressOnly } =
    draftAddress;
  const addressStructure = {
    name_line: "",
    thoroughfare: "",
    premise: "",
    locality: "",
    administrative_area: "",
    postal_code: "",
    country: "",
    ...draftAddressOnly
  };
  const profile = useSelector(state =>
    organizationProfileSelector(state, { organizationId })
  );
  const companyNameRedux = company_name || profile?.company_name;
  const billingContactRedux = billing_contact || profile?.billing_contact;
  const vatNumberRedux = vat_number || profile?.vat_number;

  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState(addressStructure);
  const [companyName, setCompanyName] = useState(companyNameRedux);
  const [billingContact, setBillingContact] = useState(
    billingContactRedux || draftAddress
  );
  const [originalCompanyName, setOriginalCompanyName] =
    useState(companyNameRedux);
  const [originalBillingContact, setOriginalBillingContact] =
    useState(billingContactRedux);
  const [vatNumber, setVatNumber] = useState(vatNumberRedux);
  const [showButtons, setShowButtons] = useState(false);
  const [error, setError] = useState("");
  const loadedAddressWithProfileInfo = {
    ...loadedAddress,
    company_name: profile?.company_name,
    billing_contact: profile?.billing_contact,
    vat_number: profile?.vat_number
  };

  const setAddressData = data => {
    const addressObj = {
      name_line: data?.name_line,
      thoroughfare: data?.thoroughfare,
      premise: data?.premise,
      locality: data?.locality,
      administrative_area: data?.administrative_area,
      postal_code: data?.postal_code,
      country: data?.country,
      ...draftAddressOnly
    };
    setAddress(addressObj);
  };

  const organization = useSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const orgId = organization?.id;

  const supportUrl = getUrl({
    key: "users.tickets",
    props: { username: organization?.name || organizationId }
  });

  useEffect(() => {
    if (
      Object.entries(draftAddress).some(
        ([key, value]) => value && value !== loadedAddressWithProfileInfo?.[key]
      )
    ) {
      setShowButtons(true);
    }
  }, []);

  useEffect(() => {
    const getAddress = async () => {
      setAddressData(addressData);
    };
    getAddress();
  }, [orgId, addressData]);

  useEffect(() => {
    setBillingContact(billingContactRedux);
    setCompanyName(companyNameRedux);
    setVatNumber(vatNumberRedux);
  }, [billingContactRedux, companyNameRedux, vatNumberRedux]);

  const countries = useMemo(() => countryList().getData(), []);

  const showVat = country => vatCountries.find(i => i === country);

  const updateAddressField = (value, field) => {
    onDraftAddressChange(prevState => ({
      ...prevState,
      [field]: value
    }));
    setAddress(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const onNameChange = e => {
    updateAddressField(e.target.value, "name_line");
  };

  const onVatNumberChange = e => {
    onDraftAddressChange(prevState => ({
      ...prevState,
      vat_number: e.target.value
    }));
    setVatNumber(e.target.value);
  };

  const onCompanyNameChange = e => {
    onDraftAddressChange(prevState => ({
      ...prevState,
      company_name: e.target.value
    }));
    setCompanyName(e.target.value);
  };

  const onBillingContactChange = e => {
    onDraftAddressChange(prevState => ({
      ...prevState,
      billing_contact: e.target.value
    }));
    setBillingContact(e.target.value);
  };

  const onCountryChange = ({ value }) => {
    setShowButtons(true);
    updateAddressField(value, "country");
  };

  const onThoroughfareChange = e => {
    updateAddressField(e.target.value, "thoroughfare");
  };

  const onPremiseChange = e => {
    updateAddressField(e.target.value, "premise");
  };

  const onLocalityChange = e => {
    updateAddressField(e.target.value, "locality");
  };

  const onAdministrativeAreaChange = e => {
    updateAddressField(e.target.value, "administrative_area");
  };

  const onPostalCodeChange = e => {
    updateAddressField(e.target.value, "postal_code");
  };

  const save = async () => {
    setSaving(true);
    setError("");

    const requiredFields = structuredClone(address);
    delete requiredFields.premise;
    delete requiredFields.vat_number;
    delete requiredFields.company_name;
    delete requiredFields.billing_contact;

    if (!Object.values(requiredFields).every(Boolean)) {
      setError("t:billing.details.errors.inputs");
      return setSaving(false);
    }

    const {
      country,
      name_line,
      locality,
      premise,
      administrative_area,
      thoroughfare,
      postal_code
    } = address;

    try {
      if (address && Object.keys(address).length) {
        const newAddress = await dispatch(
          updateOrganizationAddress({
            organizationId: orgId,
            updates: {
              name_line,
              country,
              locality,
              premise,
              administrative_area,
              thoroughfare,
              postal_code
            }
          })
        );

        if (newAddress.type === updateOrganizationAddress.fulfilled.type) {
          setAddressData(newAddress.payload);
        }
      }
      if (vatNumber || companyName || billingContact) {
        const updatedProfile = await client.updateOrganizationProfile(orgId, {
          vat_number: vatNumber,
          company_name: companyName,
          billing_contact: billingContact
        });
        setCompanyName(updatedProfile.company_name);
        setBillingContact(updatedProfile.billing_contact);
        setOriginalCompanyName(updatedProfile.company_name);
        setOriginalBillingContact(updatedProfile.billing_contact);
        setVatNumber(updatedProfile.vat_number);
      }
      onAddressChange &&
        onAddressChange({
          thoroughfare,
          name_line,
          country
        });
      onEmailChange && onEmailChange(billingContact);
      setShowButtons(false);
      setSaving(false);

      finalize && finalize();
    } catch (err) {
      setError(err.title);
      logger(err);
      setSaving(false);
    }
  };

  const resetForm = () => {
    onDraftAddressChange({
      ...loadedAddressWithProfileInfo,
      company_name: profile?.company_name,
      billing_contact: profile?.billing_contact,
      vat_number: profile?.vat_number
    });
    setAddress(loadedAddress);
    setCompanyName(originalCompanyName);
    setBillingContact(originalBillingContact);
    setVatNumber(vatNumberRedux);
    setShowButtons(false);
  };

  const submitButtonDisabled = !(
    address.name_line &&
    address.country &&
    address.thoroughfare &&
    address.locality &&
    address.administrative_area &&
    address.postal_code &&
    billingContact &&
    companyName
  );

  const {
    name_line,
    country,
    thoroughfare,
    locality,
    premise,
    administrative_area,
    postal_code
  } = address;

  const countrySelection = useMemo(
    () =>
      countries.find(
        elt => elt.value?.toLowerCase() === country?.toLowerCase()
      ) ?? null,
    [country]
  );

  return (
    <Layout>
      <PaymentWrapper>
        {error && (
          <Error>
            {error.startsWith("t:")
              ? intl.formatMessage({ id: error.replace("t:", "") })
              : error}
          </Error>
        )}
        <AddressWrapper
          id="billing-address-form"
          className="address-form"
          onChange={() => setShowButtons(true)}
        >
          <Input
            data-pendo-redact-text
            id="name_line"
            name="name_line"
            label={intl.formatMessage({ id: "full_name" })}
            placeholder={intl.formatMessage({ id: "full_name" })}
            className="name_line"
            value={name_line}
            onChange={onNameChange}
            disabled={!organization?.hasLink("address")}
            required
            width="100%"
          />
          <Input
            data-pendo-redact-text
            id="billing_contact"
            name="billing_contact"
            label={intl.formatMessage({ id: "billing_contact_email" })}
            placeholder={intl.formatMessage({ id: "billing_contact_email" })}
            className="billing_contact"
            value={billingContact}
            onChange={onBillingContactChange}
            disabled={!organization?.hasLink("profile")}
            required
            width="100%"
          />
          {showVat(country) && (
            <Input
              data-pendo-redact-text
              id="vat_number"
              name="vat_number"
              label={intl.formatMessage({ id: "vat_number" })}
              placeholder={intl.formatMessage({ id: "vat_number" })}
              className="vat_number"
              value={vatNumber}
              onChange={onVatNumberChange}
              required={false}
              disabled={!organization?.hasLink("profile")}
              width="100%"
            />
          )}
          <SelectWrapper>
            <Label
              htmlFor="countries-address-form"
              required
              disabled={!organization?.hasLink("address") || disableCountry}
            >
              {intl.formatMessage({ id: "country" })}
            </Label>
            <Select
              id="countries-address-form"
              options={countries}
              value={countrySelection}
              onChange={onCountryChange}
              placeholder={intl.formatMessage({ id: "country" })}
              required
              isDisabled={!organization?.hasLink("address") || disableCountry}
            />
          </SelectWrapper>
          {disableCountry ? (
            <Note>
              {intl.formatMessage(
                { id: "billing.details.note.country" },
                {
                  Link: txt => (
                    <Link
                      href={supportUrl || ""}
                      analyticId="billing.details.note.country"
                    >
                      {txt}
                    </Link>
                  )
                }
              )}
            </Note>
          ) : (
            <div />
          )}
          <Input
            data-pendo-redact-text
            id="company_name"
            name="company_name"
            label={intl.formatMessage({ id: "company" })}
            placeholder={intl.formatMessage({ id: "company_name" })}
            value={companyName}
            onChange={onCompanyNameChange}
            disabled={!organization?.hasLink("profile")}
            required
            width="100%"
          />
          {!showVat(country) && <div />}
          <Input
            data-pendo-redact-text
            id="thoroughfare"
            name="thoroughfare"
            label={intl.formatMessage({ id: "billing_address" })}
            placeholder={intl.formatMessage({ id: "address" })}
            className="address"
            value={thoroughfare}
            onChange={onThoroughfareChange}
            required={true}
            disabled={!organization?.hasLink("address")}
            width="100%"
          />
          <Input
            data-pendo-redact-text
            id="premise"
            name="premise"
            label={intl.formatMessage({ id: "billing_address2" })}
            placeholder={intl.formatMessage({ id: "address2" })}
            className="address"
            value={premise}
            onChange={onPremiseChange}
            required={false}
            disabled={!organization?.hasLink("address")}
            width="100%"
          />
          <Input
            data-pendo-redact-text
            id="locality"
            name="locality"
            label={intl.formatMessage({ id: "city" })}
            placeholder={intl.formatMessage({ id: "city" })}
            className="locality"
            value={locality}
            onChange={onLocalityChange}
            disabled={!organization?.hasLink("address")}
            required
            width="100%"
          />
          <InlineFields>
            <Input
              data-pendo-redact-text
              id="administrative_area"
              name="administrative_area"
              label={intl.formatMessage({
                id: "state_province_region"
              })}
              placeholder={intl.formatMessage({
                id: "state_province_region.placeholder"
              })}
              className="administrative_area"
              value={administrative_area}
              onChange={onAdministrativeAreaChange}
              disabled={!organization?.hasLink("address")}
              required
              width="100%"
            />
            <Input
              data-pendo-redact-text
              id="postal_code"
              name="postal_code"
              label={intl.formatMessage({ id: "zip_postal_code" })}
              placeholder={intl.formatMessage({
                id: "zip_postal_code.placeholder"
              })}
              className="postal_code"
              value={postal_code}
              onChange={onPostalCodeChange}
              disabled={!organization?.hasLink("address")}
              required
              width="100%"
            />
          </InlineFields>
        </AddressWrapper>
      </PaymentWrapper>
      {showButtons && (
        <div className="action-buttons-wrapper">
          {saving ? (
            <Loading />
          ) : (
            <div className="action-buttons">
              <Button
                analyticId="save"
                id="update-address-submit"
                onClick={save}
                aria-label={intl.formatMessage({ id: "save" })}
                disabled={submitButtonDisabled}
              >
                {capitalize(intl.formatMessage({ id: "save" }))}
              </Button>
              <Button
                id="create-project-back"
                variant="secondary"
                onClick={resetForm}
                analyticId="cancel"
                aria-label={intl.formatMessage({ id: "cancel" })}
              >
                {capitalize(intl.formatMessage({ id: "cancel" }))}
              </Button>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

AddressForm.propTypes = {
  organizationId: PropTypes.string,
  finalize: PropTypes.func,
  addressData: PropTypes.object,
  loadedAddress: PropTypes.object,
  disableCountry: PropTypes.bool,
  onEmailChange: PropTypes.func,
  onAddressChange: PropTypes.func,
  onDraftAddressChange: PropTypes.func,
  draftAddress: PropTypes.object
};

export default AddressForm;
